import { useMemo } from 'react'
import type { CSSProperties, JSX, ReactNode } from 'react'

import { ChannelAvatar, Flex, Table, Text } from '@buffer-mono/popcorn'

import { ColumnPickerPopover } from '../../components/ColumnPickerPopover'
import { MetricValue } from '../../components/MetricValue'
import { PaginationControls } from '../../components/PaginationControls'
import { SortableHeaderCell } from '../../components/SortableHeaderCell'
import { useColumnVisibility } from '../../hooks'
import type { ColumnVisibilityScope } from '../../hooks'
import type { ChannelInsight } from '../hooks/useAllChannelsInsightsSummary'

import styles from './AllChannelsPerformanceTable.module.css'

export const CHANNELS_PER_PAGE = 5

const findMetric = (
  metrics: ChannelInsight['metrics'],
  type: string,
): ChannelInsight['metrics'][number] | undefined => {
  return metrics.find((m) => m.type === type)
}

export type PerformanceTableSort = { key: string; direction: 'asc' | 'desc' }

export type PerformanceTableViewProps = Readonly<{
  channelInsights: ChannelInsight[]
  scopeId: ColumnVisibilityScope
  sortConfig: PerformanceTableSort
  onSort: (metricType: string) => void
  page: number
  onPageChange: (page: number) => void
  refreshing?: boolean
  renderRowAction?: (insight: ChannelInsight) => ReactNode
  'aria-label'?: string
}>

export function PerformanceTableView({
  channelInsights,
  scopeId,
  sortConfig,
  onSort,
  page,
  onPageChange,
  refreshing = false,
  renderRowAction,
  'aria-label': ariaLabel,
}: PerformanceTableViewProps): JSX.Element {
  const metrics = useMemo(
    () => channelInsights.flatMap((insight) => insight.metrics),
    [channelInsights],
  )
  const {
    availableMetrics,
    visibleMetrics,
    selectedMetrics,
    toggleMetric,
    reorderMetric,
    showColumnSelector,
  } = useColumnVisibility({
    availableMetrics: metrics,
    scopeId,
  })

  const sortedInsights = useMemo(() => {
    return channelInsights.toSorted((a, b) => {
      const aMetric = a.metrics.find((m) => m.type === sortConfig.key)
      const bMetric = b.metrics.find((m) => m.type === sortConfig.key)
      const aVal = aMetric?.value ?? -1
      const bVal = bMetric?.value ?? -1
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
    })
  }, [channelInsights, sortConfig])

  const totalPages = Math.max(
    1,
    Math.ceil(sortedInsights.length / CHANNELS_PER_PAGE),
  )
  // Clamp so a shrinking dataset (e.g. a channel filter) never strands the view
  // on a page that no longer exists.
  const safePage = Math.min(page, totalPages)
  const visibleInsights = sortedInsights.slice(
    (safePage - 1) * CHANNELS_PER_PAGE,
    safePage * CHANNELS_PER_PAGE,
  )

  const tableStyle: CSSProperties & { '--perf-table-columns': number } = {
    '--perf-table-columns': visibleMetrics.length,
  }

  return (
    <>
      <Table
        className={styles.performanceTable}
        // Drives the table's min-width so its columns keep their widths and the
        // wrapper scrolls horizontally on narrow viewports (see the CSS).
        style={tableStyle}
        aria-label={ariaLabel}
      >
        {/*
          Fixed column widths keep the layout static: with the default `auto`
          layout the browser re-derives every column width from whatever rows
          are currently visible, so changing the sort (which swaps which rows
          and values are on screen) made the columns jump. The `<colgroup>`
          pins each metric/actions column; the Channel column has no width so it
          absorbs the remaining space.
        */}
        <colgroup>
          <col />
          {visibleMetrics.map(({ type }) => (
            <col key={type} className={styles.metricCol} />
          ))}
          <col className={styles.actionsCol} />
        </colgroup>
        <Table.Header className={styles.tableHeader}>
          <Table.Row className={styles.tableHeaderRow}>
            <Table.HeaderCell align="left" className={styles.channelCell}>
              Channel
            </Table.HeaderCell>
            {visibleMetrics.map(({ type, displayName }) => (
              <SortableHeaderCell
                key={type}
                sortKey={type}
                label={displayName}
                activeSortKey={sortConfig.key}
                sortDirection={sortConfig.direction}
                onSort={onSort}
              />
            ))}
            <Table.HeaderCell align="right" className={styles.actionsColumn}>
              {showColumnSelector && (
                <ColumnPickerPopover
                  availableMetrics={availableMetrics}
                  selectedMetrics={selectedMetrics}
                  onToggle={toggleMetric}
                  onReorder={reorderMetric}
                />
              )}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {visibleInsights.map((insight) => (
            <Table.Row key={insight.channel.id} className={styles.channelRow}>
              <Table.Cell
                className={`${styles.channelCell} ${styles.postCell}`}
              >
                <Flex gap="sm" align="center" translate="no">
                  <ChannelAvatar
                    channel={insight.channel.service}
                    src={insight.channel.avatar}
                    alt={insight.channel.name}
                    size="small"
                  />
                  <Text truncate className={styles.channelName}>
                    {insight.channel.name}
                  </Text>
                </Flex>
              </Table.Cell>
              {visibleMetrics.map(({ type }) => {
                const metric = findMetric(insight.metrics, type)
                return (
                  <Table.Cell
                    key={type}
                    align="left"
                    className={styles.postCell}
                  >
                    <MetricValue
                      metric={metric}
                      loading={refreshing}
                      size="small"
                    />
                  </Table.Cell>
                )
              })}
              <Table.Cell
                align="right"
                className={`${styles.actionsColumn} ${styles.postCell}`}
              >
                {renderRowAction?.(insight)}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <PaginationControls
            page={safePage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            loading={refreshing}
          />
        </div>
      )}
    </>
  )
}
